import {
  CustomerSegmentPeriod,
  CustomerSegmentPeriodKey,
  OrderSummaryAllowedKey,
  OrderSummaryConfig,
  ParsedOrderCompositionPrdocut,
  PriceConfig,
  PriceDetail,
  PriceDetailKey,
  SalesFunnelPeriods,
  SalesConfigKey,
  SalesOrdersAllocationOrder,
  OrganicFunnelOrdersAllocationOrder,
  OrganicFunnelPeriods,
  OrganicConfigKey,
  MarketingFunnelPeriods,
  MarketingConfigKey,
  GETOrderSummaryResponse,
  MarketingOrdersAllocationOrder,
  OrganicDirectResidualOrdersOrder
} from '@/feature/financial-model/ecommerce/revenues/api/types'
import { CogsSelectableKeys, DirectCostUnitCost, DirectLabourPeriod, DirectLabourTableRows } from '@/feature/financial-model/ecommerce/cogs/api/types'
import { GetPersonnelDataResponse, TableRows } from '@/feature/financial-model/ecommerce/personnel/api/types'
import { OicPeriod } from '@/feature/financial-model/ecommerce/oic/api/types'
import { OpexPeriod } from '@/feature/financial-model/ecommerce/opex/api/types'
import { InventoryPeriod, InventoryPayloadKey } from '@/feature/financial-model/ecommerce/twc/types/inventory'
import { PayableBaseFieldsKeys, PayablePeriod } from '@/feature/financial-model/ecommerce/twc/types/payable'
import { ReceivableBaseFieldsKeys, ReceivablePeriod } from '@/feature/financial-model/ecommerce/twc/types/receivable'
import { AssetTypeEntryPeriod, AssetTypeEntryValues } from '@/feature/financial-model/ecommerce/fixed-assets/api/types'
import { BadDebtBaseFieldsKeys, BadDebtPeriod } from '@/feature/financial-model/ecommerce/provisions/types/bad-debt'
import { ProvisionInventoryBaseFieldsKeys, ProvisionInventoryPeriod } from '@/feature/financial-model/ecommerce/provisions/types/provision-inentory'
import { OtherProvisionsBaseFieldsKeys, OtherProvisionsPeriod } from '@/feature/financial-model/ecommerce/provisions/types/other-provisions'
import { CurrentAssetsBaseFieldsKeys, CurrentAssetsPeriod, OwcAssetsConfigType } from '@/feature/financial-model/ecommerce/owc/types/current-assets'
import { CurrentLiabilitiesBaseFieldsKeys, CurrentLiabilitiesPeriod } from '@/feature/financial-model/ecommerce/owc/types/current-liabilities'
import { EquityBaseFieldsKeys, EquityPeriod } from '@/feature/financial-model/ecommerce/capital-structure/types/equity'
import { GrantBaseFieldsKeys, GrantPeriod } from '@/feature/financial-model/ecommerce/capital-structure/types/grant'
import { PeriodType, PeriodTypeQuery, PeriodTypeView } from '@/types'
import { InputVATModule, OutputVATModule, VatBaseFieldsKeys, VatPeriod, VAT_TYPE } from '@/feature/financial-model/ecommerce/vat/types'

type Unsubscribe = () => void

export type SidePanelProduct = {
  productId: string
  productName: string
  prices: PriceDetail[]
  config: PriceConfig
}

export type SidePanelOrderProduct = {
  productId: string
  productName: string
  config: OrderSummaryConfig
  orders: ParsedOrderCompositionPrdocut[]
}

export type SidePanelOrderPeriod = GETOrderSummaryResponse['orders_summary'][number]['data'][number]

export type HistoricalValue = {
  fiscal_year: number
  fiscal_month: number
  period: string
  period_type: PeriodType
  [key: string]: number | string | null | undefined
}

export type SidePanelPayload = {
  itemName?: string
  product?: SidePanelProduct
  historicalPrices?: PriceDetail[]
  customerSegmentId?: string | null
  mutateDataKey?:
    | PriceDetailKey
    | SalesConfigKey
    | MarketingConfigKey
    | OrganicConfigKey
    | OrderSummaryAllowedKey
    | CogsSelectableKeys
    | keyof DirectLabourTableRows
    | keyof TableRows
    | `input_value`
    | ReceivableBaseFieldsKeys
    | PayableBaseFieldsKeys
    | Exclude<InventoryPayloadKey, `config`>
    | keyof AssetTypeEntryValues
    | VatBaseFieldsKeys
    | BadDebtBaseFieldsKeys
    | ProvisionInventoryBaseFieldsKeys
    | OtherProvisionsBaseFieldsKeys
    | ProvisionInventoryBaseFieldsKeys
    | CurrentAssetsBaseFieldsKeys
    | CurrentLiabilitiesBaseFieldsKeys
    | EquityBaseFieldsKeys
    | GrantBaseFieldsKeys
  customerSegmentRowKey?: CustomerSegmentPeriodKey
  customerSegmentPeriods?: CustomerSegmentPeriod[]
  rowName?: string
  queryPeriod?: PeriodTypeQuery
  view?: PeriodTypeView
  financialModelId?: string
  orderProduct?: SidePanelOrderProduct
  orderDataPeriods?: SidePanelOrderPeriod[]
  directCostPeriods?: DirectCostUnitCost[]
  directCostCategoryId?: string | null
  directLabourPeriods?: DirectLabourPeriod[]
  oicCategoryId?: string
  oicPeriods?: OicPeriod[]
  opexCategoryId?: string
  opexPeriods?: OpexPeriod[]
  departmentId?: string
  personnelPeriods?: GetPersonnelDataResponse['periods']
  receivablePeriods?: ReceivablePeriod[]
  receivableProductId?: string | null
  receivableCustomerSegmentId?: string | null
  inventoryPeriods?: InventoryPeriod[]
  inventoryProductId?: string | null
  inventoryCategoryId?: string | null
  payablePeriods?: PayablePeriod[]
  payableProductId?: string | null
  payableCostCategoryId?: string | null
  payableSetupId?: string
  fixedAssetPeriods?: AssetTypeEntryPeriod[]
  fixedAssetAssetTypeId?: string
  vatPeriods?: VatPeriod[]
  equityPeriods?: EquityPeriod[]
  grantPeriods?: GrantPeriod[]
  vatModule?: InputVATModule | OutputVATModule
  vatType?: VAT_TYPE
  provisionPeriods?: BadDebtPeriod[]
  provisionProductId?: string | null
  provisionCategoryId?: string | null
  provisionInventoryPeriods?: ProvisionInventoryPeriod[]
  provisionInventoryProductId?: string | null
  provisionInventoryCategoryId?: string | null
  otherProvisionsPeriods?: OtherProvisionsPeriod[]
  otherProvisionsProductId?: string | null
  otherProvisionsCategoryId?: string | null
  owcPeriods?: (CurrentAssetsPeriod | CurrentLiabilitiesPeriod)[]
  owcCategoryId?: string | null
  owcConfigType?: OwcAssetsConfigType
  salesFunnel?: SalesFunnelPeriods
  expense_category_id?: string | null
  marketingFunnel?: MarketingFunnelPeriods
  organicFunnel?: OrganicFunnelPeriods
  marketingOrderAllocationPeriods?: MarketingOrdersAllocationOrder[]
  organicOrdersAllocationPeriods?: OrganicFunnelOrdersAllocationOrder[]
  organicDirectResidualOrdersPeriods?: OrganicDirectResidualOrdersOrder[]
  salesOrdersAllocationPeriods?: SalesOrdersAllocationOrder[]
  orderTypeId?: string | null
  funnelId?: string | null
  historicalValues?: HistoricalValue[]
  periodValuesByKey?: Record<string, number>
}

type Listener = (payload?: SidePanelPayload) => void

const listeners: Listener[] = []

export const subscribeSidePanelOpen = (cb: Listener): Unsubscribe => {
  listeners.push(cb)

  return () => {
    const idx = listeners.indexOf(cb)
    if (idx >= 0) listeners.splice(idx, 1)
  }
}

export const openForecastRulePanel = (payload?: any) => {
  const payloadToSend = payload ?? {}
  listeners.slice().forEach(cb => cb(payloadToSend))
}

const sidePanelEmitter = { subscribeSidePanelOpen, openForecastRulePanel }

export default sidePanelEmitter
